import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Header from '../../components/Header';
import { api } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { CertDayCounter } from '../../components/CertDayCounter';
// work_date / cert_start_date arrive as plain YYYY-MM-DD, and `new Date()`
// reads those as UTC midnight — the previous day, in Alberta. A local
// formatDayString handled that correctly and then finished on a bare
// toLocaleDateString(), so what the card printed depended on the phone
// holding it: 2026-02-14 to one hand, 2/14/2026 to the next, for the same
// shift. formatCalendarDate carries the local parse AND a pinned month name.
import { formatCalendarDate } from '../../lib/calendar-date';
import {
  ArrowLeft,
  Wrench,
  Shield,
  ClipboardCheck,
  ClipboardList,
  AlertTriangle,
  Eye,
  Loader2,
  History,
} from 'lucide-react';
import { useBack } from '../../hooks/useBack';
import { EquipmentHistoryCard, LastJobLine } from '../../components/equipment/EquipmentHistory';
import { parseLastJob } from '../../lib/equipment-history';

export default function FieldEquipmentDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const goBack = useBack('/field/equipment');
  // Equipment inspections are a casing-running product — see the same guard on
  // the desktop equipment detail page.
  const { hasVertical } = useAuth();

  // One round trip for everything this screen renders: the equipment row, the
  // certification block and the most recent slice of the usage log. Field crews
  // are on bad LTE, so this deliberately does not fan out into separate calls.
  const {
    data: equipment,
    isLoading: detailLoading,
    isError: detailError,
  } = useQuery({
    queryKey: ['field-equipment-detail', id],
    queryFn: () => api.employee.myEquipmentDetail(id!).then(r => r.data?.data ?? null),
    enabled: !!id,
  });

  // `lastJob` (api#985) rides on GET /equipment/:id, not on the field detail
  // payload. Same key and unwrap as the office detail page, so the two share a
  // cache entry. Any tenant user may read it. The line renders nothing until it
  // arrives, so a slow or failed read costs the screen nothing else.
  const { data: officeRow } = useQuery({
    queryKey: ['equipment', id],
    queryFn: () => api.equipment.get(id!).then(r => r.data.data ?? r.data),
    enabled: !!id,
  });
  const lastJob = parseLastJob(officeRow);

  // Cert day tracking is computed server-side by getEquipmentCertUsage() — the
  // same service the desktop card reads — so the two surfaces cannot drift.
  // Nothing here recomputes days or percentages.
  const certification = (equipment as any)?.certification ?? null;
  const certName: string | null = certification?.cert_name ?? null;
  const daysUsed: number | null = certification?.days_used ?? null;
  const daysRequired: number | null = certification?.days_required ?? null;
  const percentUsed: number | null = certification?.percent_used ?? null;
  const certStartDate: string | null = certification?.start_date ?? null;
  // 1h R2 presign delivered with the detail payload — opening it needs no
  // second round trip for a download token.
  const certificateUrl: string | null = certification?.certificate_url ?? null;

  // Tracking renders whenever the tool HAS a cert budget — the limit alone
  // decides, not whether anyone has counted against it yet.
  //
  // This used to also require `percentUsed != null`, which was correct while the
  // API sent 0 for an uncounted tool and wrong the moment it started sending
  // null (API #367). An uncounted tool with a 100-day limit then rendered no
  // tracking block at all, and a missing block reads as "this tool has no cert
  // tracking" — exactly the opposite of the truth for a tool that has a limit
  // and has never been counted against it.
  //
  // A NULL COUNTER IS NOT A MISSING SECTION, IT IS A MISSING NUMBER. CertDayCounter
  // already renders that as an em dash with a grey track, the same as it does on
  // Certification Tracking, the equipment list and equipment detail. Letting it
  // do so here is what makes "absent renders as —" true on all five surfaces
  // rather than four.
  const showTracking = daysRequired != null;

  const usageLog: any[] = Array.isArray((equipment as any)?.usage_log) ? (equipment as any).usage_log : [];
  const usageTotal: number = (equipment as any)?.usage_log_total_count ?? usageLog.length;

  const checkStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      pass: 'Pass',
      fail: 'Fail',
      needs_repair: 'Needs Repair',
      left_on_site: 'Left on Site',
    };
    return labels[status] || status;
  };

  return (
    <div className="animate-fade-in min-h-full bg-fieldiq-dark pb-[var(--bottom-chrome)]">
      <div className="max-w-[1920px] mx-auto">
        <Header />
        <div className="px-4 py-3 border-b border-fieldiq-border flex items-center gap-3">
          <button
            onClick={goBack}
            aria-label="Back to equipment"
            className="text-fieldiq-text -ml-2 min-w-[44px] min-h-[44px] flex items-center justify-center"
          >
            <ArrowLeft size={26} />
          </button>
          <h1 className="text-xl font-bold text-white">Equipment Details</h1>
        </div>
      </div>

      <div className="max-w-[1920px] mx-auto px-4 py-4 space-y-4">
        {detailLoading ? (
          <div className="text-center py-12 text-fieldiq-muted">
            <Loader2 size={32} className="mx-auto mb-3 animate-spin" />
            <p className="text-sm">Loading equipment...</p>
          </div>
        ) : detailError ? (
          <div className="text-center py-12 text-fieldiq-text">
            <AlertTriangle size={40} className="mx-auto mb-3 text-amber-400" />
            <p>Could not load equipment</p>
          </div>
        ) : !equipment ? (
          <div className="text-center py-12 text-fieldiq-text">
            <Wrench size={40} className="mx-auto mb-3 text-fieldiq-muted" />
            <p>Equipment not found</p>
          </div>
        ) : (
          <>
            {/* Equipment Info Card */}
            <div className="bg-fieldiq-card rounded-xl p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-lg bg-fieldiq-accent/20 flex items-center justify-center">
                  <Wrench size={24} className="text-fieldiq-accent" />
                </div>
                <div className="flex-1">
                  <h2 className="text-lg font-bold text-white">{equipment.name}</h2>
                  <p className="text-sm text-fieldiq-muted">{equipment.unit_number} · {equipment.category}</p>
                </div>
                <StatusBadge status={equipment.status} />
              </div>

              {equipment.serial_number && (
                <div className="text-xs text-fieldiq-muted mt-1">
                  S/N: {equipment.serial_number}
                </div>
              )}

              {equipment.job_number && (
                <div className="mt-2 bg-fieldiq-dark/60 rounded-lg px-3 py-2 text-sm text-fieldiq-text">
                  Job: <span className="text-white font-medium">{equipment.job_number}</span>
                  {equipment.well_name && <span className="text-fieldiq-muted"> · {equipment.well_name}</span>}
                </div>
              )}

              {id && <LastJobLine equipmentId={id} lastJob={lastJob} variant="field" />}

              {/* Last check info */}
              {equipment.last_check_date && (
                <div className="mt-2 flex items-center gap-2 text-xs text-fieldiq-muted">
                  <ClipboardCheck size={14} />
                  Last check: {formatCalendarDate(equipment.last_check_date) ?? '—'} —{' '}
                  <span className={equipment.last_check_status === 'pass' ? 'text-green-400' : 'text-amber-400'}>
                    {checkStatusLabel(equipment.last_check_status)}
                  </span>
                </div>
              )}
            </div>

            {/* Certifications Section */}
            <div className="bg-fieldiq-card rounded-xl p-4">
              <h3 className="text-white font-semibold text-sm flex items-center gap-2 mb-3">
                <Shield size={16} className="text-fieldiq-accent" />
                Inspections
              </h3>

              {certName || showTracking ? (
                <div className="space-y-2">
                  {certName && <p className="text-sm text-fieldiq-text">{certName}</p>}

                  {/* Day tracking — hidden only when the equipment has no cert
                      day limit at all. A tool that HAS a limit shows the block
                      whether or not anyone has counted against it; an uncounted
                      one shows a dash. All three numbers come straight from the
                      API; the percent is passed through rather than recomputed
                      so this screen and the desktop card cannot disagree by a
                      rounding step. A null percent is not consulted — the
                      counter resolves it to null itself.

                      Parity with the desktop page used to be a comment asking
                      the next editor to keep four copies of the thresholds in
                      step. It is now structural: one lib, one phrasing, one
                      absent rule. */}
                  {showTracking && (
                    <div className="pt-1">
                      <CertDayCounter
                        daysUsed={daysUsed}
                        dayLimit={daysRequired}
                        mode="used"
                        percentUsed={percentUsed}
                      />
                      {formatCalendarDate(certStartDate) && (
                        <p className="text-xs text-fieldiq-muted mt-1.5">
                          Started {formatCalendarDate(certStartDate)}
                        </p>
                      )}
                    </div>
                  )}

                  {certificateUrl ? (
                    <a
                      href={certificateUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 w-full min-h-[44px] flex items-center justify-center gap-2 bg-green-500/10 text-green-400 py-2.5 rounded-lg text-sm font-medium"
                    >
                      <Eye size={16} />
                      View Certificate
                    </a>
                  ) : (
                    <p className="text-xs text-amber-400 mt-2 flex items-center gap-1">
                      <AlertTriangle size={14} /> No certificate uploaded
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-fieldiq-muted">No certification requirements set for this equipment</p>
              )}
            </div>

            {/* Usage Log Section */}
            <div className="bg-fieldiq-card rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <History size={16} className="text-fieldiq-accent" />
                <div>
                  <h3 className="text-white font-semibold text-sm">Usage Log</h3>
                  <p className="text-xs text-fieldiq-muted mt-0.5">
                    {usageTotal} {usageTotal === 1 ? 'record' : 'records'}
                  </p>
                </div>
              </div>

              {usageLog.length === 0 ? (
                <p className="text-sm text-fieldiq-muted py-4 text-center">No usage recorded</p>
              ) : (
                <>
                  {/* A real list, announced as one. These are stacked divs, so
                      a screen reader read them as an undifferentiated run of
                      text with no sense of how many days there were or where
                      one ended. It also gives each card a name a test can
                      address, instead of reaching for `.closest()` — which
                      `testing-library/no-node-access` forbids and which is a
                      worse way to say "the card this text is in" regardless. */}
                  <div className="space-y-2" role="list" aria-label="Certification day usage">
                    {usageLog.map((row: any, i: number) => {
                      // Omit missing segments rather than printing an em dash.
                      const meta = [row.rig_name, row.job_number, row.well_name].filter(Boolean);
                      // A day the office entered by hand from a paper log,
                      // rather than one counted off an approved ticket. Marked
                      // here for the same reason it is marked on the cert PDF:
                      // the hand reading this list is the person who ran the
                      // tool, and an office-entered day sitting unmarked among
                      // their own runs is what makes them doubt the whole list.
                      //
                      // MARKED QUIETLY, and the card looks like any other. It
                      // shipped with an amber fill, an amber border and a bold
                      // banner, which made the annotation louder than the run
                      // it annotates — on a phone, louder than anything else on
                      // the screen. Same treatment as the office ledger and the
                      // certification PDF now, so the surfaces a hand and the
                      // office might compare cannot disagree about how much
                      // this matters.
                      //
                      // The voided card keeps its red, deliberately. That one
                      // is a WARNING — a day charged against a ticket that no
                      // longer exists. This is PROVENANCE. They must not look
                      // alike.
                      const isHistorical = row.source === 'historical';
                      return (
                        <div
                          key={row.id ?? i}
                          role="listitem"
                          className={`rounded-lg px-3 py-2.5 ${
                            row.ticket_deleted
                              ? 'bg-red-500/10 border border-red-500/40'
                              : 'bg-fieldiq-dark/60'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <span className="text-sm text-white">
                              {row.ticket_deleted
                                ? <s>{formatCalendarDate(row.date) ?? '—'}</s>
                                : (formatCalendarDate(row.date) ?? '—')}
                              {/* `> 1`, not truthiness: every ordinary row is
                                  one day and printing "· 1 day" on all of them
                                  is noise on a phone. The multi-day case is the
                                  one a reader cannot infer from the row count. */}
                              {row.days_used > 1 && (
                                <span className="text-xs text-fieldiq-muted"> · {row.days_used} days</span>
                              )}
                              {/* A correction carries a NEGATIVE day count and
                                  an opening balance can carry 0, neither of
                                  which the `> 1` rule above would ever print.
                                  On a hand-entered row the number IS the fact,
                                  so it is always shown. */}
                              {isHistorical && row.days_used <= 1 && (
                                <span className="text-xs text-fieldiq-muted"> · {row.days_used} day{Math.abs(row.days_used) === 1 ? '' : 's'}</span>
                              )}
                            </span>
                            {row.ticket_number && (
                              <span className="text-xs text-fieldiq-muted text-right break-words">
                                {row.ticket_number}
                              </span>
                            )}
                          </div>
                          {/* Spelled out, not a badge. Whoever reads this on a
                              phone in a lease road has no legend to consult:
                              the day is still charged against the certification
                              allowance, which is why the row is kept at all. */}
                          {row.ticket_deleted && (
                            <p className="text-xs font-semibold text-red-300 mt-1">
                              Voided — ticket deleted, day still counted
                            </p>
                          )}
                          {/* `text-xs text-fieldiq-muted` — the same type as the
                              client name and the rig/job/well line below, so it
                              reads as one more detail about the row rather than
                              as an alert about it. No weight, no colour, no
                              caps.

                              Two words rather than the kind, matching the office
                              table and the certification PDF. Out here the kind
                              would be noise anyway: a hand wants to know the day
                              was not theirs to remember, not whether the office
                              filed it as an opening balance. */}
                          {!row.ticket_deleted && isHistorical && (
                            <p className="text-xs text-fieldiq-muted mt-1">Manual entry</p>
                          )}
                          {row.client_name && (
                            <p className="text-xs text-fieldiq-text mt-1 break-words">{row.client_name}</p>
                          )}
                          {meta.length > 0 && (
                            <p className="text-xs text-fieldiq-muted mt-1 break-words">
                              {meta.join(' · ')}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {usageTotal > usageLog.length && (
                    <p className="text-xs text-fieldiq-muted text-center mt-3">
                      Showing {usageLog.length} of {usageTotal}
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Sign-out history (api#985) — where it went and who moved it. Not
                the Usage Log above, which counts cert days. */}
            {id && <EquipmentHistoryCard equipmentId={id} variant="field" />}

            {/* Run Check / Run Inspection */}
            <div className="space-y-3">
              <button
                onClick={() => navigate('/field/equipment/check', { state: { equipment } })}
                className="w-full bg-fieldiq-accent text-black font-bold py-3 rounded-xl text-sm flex items-center justify-center gap-2"
              >
                <ClipboardCheck size={18} />
                Run Equipment Check
              </button>

              {/* The equipment check is the quick pass/fail every vertical runs.
                  A casing inspection is the long form, and it opens on the
                  checklist this equipment's type calls for rather than making
                  the tech pick it. */}
              {hasVertical('casing_running') && (
                <button
                  onClick={() => navigate('/field/safety/inspection', {
                    state: {
                      equipmentId: equipment.id ?? id,
                      equipmentType: equipment.type ?? equipment.equipment_type ?? equipment.category ?? null,
                      equipmentData: {
                        name: equipment.name ?? null,
                        asset_number: equipment.unit_number ?? null,
                        manufacturer: equipment.manufacturer ?? null,
                        model: equipment.model ?? null,
                        serial_number: equipment.serial_number ?? null,
                        size: equipment.size ?? null,
                        capacity: equipment.capacity ?? null,
                        location: equipment.location ?? equipment.well_name ?? null,
                      },
                      // The job this inspection is filed against. Same source
                      // and same precedence as FieldEquipmentScreen's card:
                      // `assigned_job_id` is `aj.id` from
                      // /employee/my-equipment/:id — the job this detail page
                      // was opened under — while `current_job_id` is a
                      // denormalised pointer that disagrees on 17 of 112 live
                      // rows. See the note on that screen for the measurement.
                      //
                      // This screen passed NO job at all until API #445, so
                      // every inspection opened from here was job-less and
                      // therefore invisible to the rest of the crew.
                      jobId: equipment.assigned_job_id ?? equipment.current_job_id ?? null,
                      jobName: equipment.job_number ?? null,
                    },
                  })}
                  className="w-full bg-fieldiq-card border border-fieldiq-border text-white font-bold py-3 rounded-xl text-sm flex items-center justify-center gap-2"
                >
                  <ClipboardList size={18} className="text-fieldiq-accent" />
                  Run Inspection
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    left_on_site: 'bg-amber-500/20 text-amber-400',
    in_use: 'bg-green-500/20 text-green-400',
    maintenance: 'bg-red-500/20 text-red-400',
    available: 'bg-blue-500/20 text-blue-400',
  };
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${styles[status] || 'bg-gray-500/20 text-gray-400'}`}>
      {(status || '').replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
    </span>
  );
}
